import { useState, useEffect } from "react";
import { Plus, Trash2, Save, Image, GripVertical, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";

export default function MediaKitSuccessCasesTab() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [newCase, setNewCase] = useState({
    title: "",
    description: "",
    image_url: "",
    order: 0,
    active: true
  });

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const response = await adminApi.get("/media-kit/success-cases/all");
      setCases(response.data.cases || []);
    } catch (error) {
      console.error("Error fetching success cases:", error);
      toast.error("Error al cargar los casos de éxito");
    } finally {
      setLoading(false);
    }
  };

  const handleAddCase = async () => {
    if (!newCase.title || !newCase.image_url) {
      toast.error("Título e imagen son requeridos");
      return;
    }

    try {
      const response = await adminApi.post("/media-kit/success-cases", {
        ...newCase,
        order: cases.length
      });
      setCases([...cases, response.data]);
      setNewCase({ title: "", description: "", image_url: "", order: 0, active: true });
      toast.success("Caso de éxito agregado");
    } catch (error) {
      console.error("Error adding success case:", error);
      toast.error("Error al agregar caso de éxito");
    }
  };

  const handleUpdateCase = (caseId, updates) => {
    // Update locally only - no API call
    setCases(cases.map(c => c.id === caseId ? { ...c, ...updates } : c));
  };

  const handleSaveCase = async (caseItem) => {
    setSavingId(caseItem.id);
    try {
      await adminApi.put(`/media-kit/success-cases/${caseItem.id}`, {
        title: caseItem.title,
        description: caseItem.description,
        image_url: caseItem.image_url,
        order: caseItem.order,
        active: caseItem.active
      });
      toast.success("Caso actualizado");
    } catch (error) {
      console.error("Error updating success case:", error);
      toast.error("Error al actualizar");
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteCase = async (caseId) => {
    if (!window.confirm("¿Eliminar este caso de éxito?")) return;

    try {
      await adminApi.delete(`/media-kit/success-cases/${caseId}`);
      setCases(cases.filter(c => c.id !== caseId));
      toast.success("Caso eliminado");
    } catch (error) {
      console.error("Error deleting success case:", error);
      toast.error("Error al eliminar");
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-400">Cargando casos de éxito...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-white">Casos de Éxito (Licenciamiento)</h3>
        <span className="text-sm text-gray-400">{cases.length} casos</span>
      </div>

      {/* Add New Case Form */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-4">
        <h4 className="font-medium text-white flex items-center gap-2">
          <Plus className="w-4 h-4" /> Agregar Nuevo Caso
        </h4>
        
        <div className="grid md:grid-cols-2 gap-4">
          <Input
            placeholder="Título del caso"
            value={newCase.title}
            onChange={(e) => setNewCase({ ...newCase, title: e.target.value })}
            className="bg-black/50 border-white/20"
          />
          <Input
            placeholder="URL de la imagen"
            value={newCase.image_url}
            onChange={(e) => setNewCase({ ...newCase, image_url: e.target.value })}
            className="bg-black/50 border-white/20"
          />
        </div>
        
        <Textarea
          placeholder="Descripción (opcional)"
          value={newCase.description}
          onChange={(e) => setNewCase({ ...newCase, description: e.target.value })}
          className="bg-black/50 border-white/20"
          rows={2}
        />

        {/* Preview */}
        {newCase.image_url && (
          <div className="flex items-center gap-4">
            <img
              src={newCase.image_url}
              alt="Preview"
              className="w-24 h-24 object-cover rounded-lg border border-white/20"
              onError={(e) => e.target.style.display = 'none'}
            />
            <span className="text-sm text-gray-400">Vista previa</span>
          </div>
        )}

        <Button onClick={handleAddCase} className="bg-[#FFE500] hover:bg-[#FFE500]/80 text-black">
          <Plus className="w-4 h-4 mr-2" /> Agregar Caso
        </Button>
      </div>

      {/* Cases List */}
      <div className="space-y-4">
        {cases.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay casos de éxito</p>
            <p className="text-sm">Agrega el primer caso arriba</p>
          </div>
        ) : (
          cases.map((caseItem) => (
            <div
              key={caseItem.id}
              className={`bg-white/5 border rounded-xl p-4 ${caseItem.active ? 'border-white/20' : 'border-red-500/30 opacity-60'}`}
            >
              <div className="flex items-start gap-4">
                {/* Drag Handle */}
                <div className="pt-2 cursor-move text-gray-500">
                  <GripVertical className="w-5 h-5" />
                </div>

                {/* Thumbnail */}
                <div className="w-32 h-24 flex-shrink-0 bg-black rounded-lg overflow-hidden">
                  {caseItem.image_url ? (
                    <img
                      src={caseItem.image_url}
                      alt={caseItem.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-600">
                      <Image className="w-8 h-8" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0 space-y-2">
                  <Input
                    value={caseItem.title}
                    onChange={(e) => handleUpdateCase(caseItem.id, { title: e.target.value })}
                    className="bg-black/50 border-white/20 font-medium"
                    placeholder="Título"
                  />
                  <Input
                    value={caseItem.image_url}
                    onChange={(e) => handleUpdateCase(caseItem.id, { image_url: e.target.value })}
                    className="bg-black/50 border-white/20 text-sm"
                    placeholder="URL de imagen"
                  />
                  <Textarea
                    value={caseItem.description || ""}
                    onChange={(e) => handleUpdateCase(caseItem.id, { description: e.target.value })}
                    className="bg-black/50 border-white/20 text-sm"
                    placeholder="Descripción"
                    rows={2}
                  />
                </div>

                {/* Actions */}
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Activo</span>
                    <Switch
                      checked={caseItem.active}
                      onCheckedChange={(checked) => handleUpdateCase(caseItem.id, { active: checked })}
                    />
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSaveCase(caseItem)}
                      disabled={savingId === caseItem.id}
                      className="text-green-600 hover:text-green-700 hover:bg-green-500/10 border-green-500/30"
                    >
                      {savingId === caseItem.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCase(caseItem.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
